import * as React from "react";
import { useTheme } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import ClampLines from "react-clamp-lines";
import { StoreState } from "../../models/states/StoreState";
import {
    setCarrouselActiveVariableIndex,
    validationClear,
    validationSetResult,
} from "../../redux/actions";
import useStyles from "./CarrouselControl.styles";
import { useLanguage } from "../../hooks/useLanguage";
import { ControlProps } from "./BaseControl";
import { translate } from "../../models/utilities/translationUtilities";
import {
    useValidation,
    useDiscreteVariadicValidator,
    isAnswerRequired,
} from "../../hooks/useValidation";
import {
    getSettingBoolean,
    getSettingNumber,
    getSettingString,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { ControlContainer } from "../../containers/controls/ControlContainer";
import { getRendererProps } from "../renderers/BaseRenderer";
import { ChoiceListControl } from "./ChoiceListControl";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { QuestionType } from "../../models/enums/QuestionType";

interface StatementProps {
    id: string;
    key: string;
    text: string;
    color: string;
    imageUrl: string;
    isMobileView: boolean;
}

const MOBILE_DIMENSIONS = 293;
const RESIZE_CAROUSEL_DESKTOP_DIMENSIONS = 460;
const RESIZE_CAROUSEL_DEFAULT_TEXT_LINES = 15;
const CROP_CONTENT_DESKTOP_SQUARE_SIZE = 372;
const CROP_CONTENT_STATEMENT_HEIGHT = 71;
const RESIZE_CAROUSEL = "resize-carousel";
const CROP_CONTENT = "crop-content";

const Statement = (props: StatementProps) => {
    const { id, color, text, imageUrl, isMobileView } = props;
    const theme: any = useTheme();
    const { ContentResponsivenessOption: contentResponsivenessOption } = theme;
    const justifyContent = imageUrl ? "space-between" : "center";
    const classes = useStyles({
        theme,
        color,
        justifyContent,
        isMobileView,
        contentResponsivenessOption,
    });
    const containerRef = React.useRef(null);
    const imageRef = React.useRef(null);
    const textContainerRef = React.useRef(null);
    const [linesToShow, setLinesToShow] = React.useState(RESIZE_CAROUSEL_DEFAULT_TEXT_LINES);
    const [forceUpdateKey, setForceUpdateKey] = React.useState(0);

    React.useEffect(() => {
        const container = containerRef.current;
        const textContainer = textContainerRef.current;
        const image = imageRef.current;
        if (image) {
            image.addEventListener("load", responsiveRender);
        } else {
            resizeFont();
            resizeCarouselStatementTextRenderer();
        }

        return () => {
            image && image.removeEventListener("load", responsiveRender);
        };

        function responsiveRender() {
            if (contentResponsivenessOption === RESIZE_CAROUSEL) {
                resizeCarousel(image, container);
            }
            cropContentRenderer(textContainer, image);
            resizeFont();
        }

        function resizeCarouselStatementTextRenderer() {
            if (contentResponsivenessOption === RESIZE_CAROUSEL) {
                const numberOfLines = getNumberOfLinesForDynamicContent(container);

                setLinesToShow(numberOfLines);
            }
        }

        function resizeFont() {
            const fontSize = getFontSize(container);
            container.style.fontSize = `${fontSize}px`;
        }
    }, [imageUrl, isMobileView, contentResponsivenessOption]);

    const cropContentRenderer = (textContainer, image) => {
        if (contentResponsivenessOption === RESIZE_CAROUSEL) {
            setForceUpdateKey((prevKey) => prevKey + 1);
            return;
        }

        textContainer.style.height = `${
            isMobileView ? MOBILE_DIMENSIONS - image.height : CROP_CONTENT_STATEMENT_HEIGHT
        }px`;

        const lineHeight = parseFloat(getComputedStyle(textContainer).lineHeight);
        const containerHeight =
            textContainer.clientHeight -
            parseFloat(getComputedStyle(textContainer).paddingTop) -
            parseFloat(getComputedStyle(textContainer).paddingBottom) -
            lineHeight;

        const calculatedLines = Math.floor(containerHeight / lineHeight);
        setLinesToShow(
            (image && isMobileView && calculatedLines >= 2) || calculatedLines <= 0
                ? 1
                : calculatedLines,
        );
        setForceUpdateKey((prevKey) => prevKey + 1);
        containerRef.current.style.height = `${
            isMobileView ? MOBILE_DIMENSIONS : CROP_CONTENT_DESKTOP_SQUARE_SIZE
        }px`;
    };

    const getNumberOfLinesForDynamicContent = (container) => {
        const containerWidth = isMobileView
            ? MOBILE_DIMENSIONS
            : RESIZE_CAROUSEL_DESKTOP_DIMENSIONS;
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        context.font = `${getFontSize(container)}px`;

        const words = text.split(" ");

        let line = "";
        let lines = 0;

        // eslint-disable-next-line no-restricted-syntax
        for (const word of words) {
            const lineWidth = context.measureText(line + word).width;

            if (lineWidth > containerWidth) {
                line = `${word} `;
                lines++;
            } else {
                line += `${word} `;
            }
        }

        // Check if there's any remaining text
        if (line.trim() !== "") {
            lines++;
        }

        canvas.remove();

        return Math.min(lines, RESIZE_CAROUSEL_DEFAULT_TEXT_LINES) + 1;
    };

    const resizeCarousel = (image, container) => {
        if (!image) return;
        const STATEMENT_RESIZE_WIDTH = 460;
        setLinesToShow(getNumberOfLinesForDynamicContent(container));
        const originalWidth = image.naturalWidth;
        const originalHeight = image.naturalHeight;
        const newHeight = (originalHeight / originalWidth) * STATEMENT_RESIZE_WIDTH;

        image.style.width = `${STATEMENT_RESIZE_WIDTH}px`;
        if (isMobileView) {
            image.style.height = "auto";
        } else {
            image.style.height = `${newHeight}px`;
        }
        container.style.width = !isMobileView ? `${STATEMENT_RESIZE_WIDTH}px` : "";
    };

    const getFontSize = (container) => {
        const DEFAULT_FONT_SIZE = 16;
        const MINIMUM_FONT_SIZE = 12;
        const MAXIMUM_FONT_SIZE = 30;
        const containerWidth = container.offsetWidth;
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        context.font = `${DEFAULT_FONT_SIZE}px sans-serif`;
        const textWidth = context.measureText(text).width;

        let fontSize = Math.floor((containerWidth / textWidth) * DEFAULT_FONT_SIZE);
        fontSize = Math.max(fontSize, MINIMUM_FONT_SIZE);
        fontSize = Math.min(fontSize, MAXIMUM_FONT_SIZE);

        if (textWidth < containerWidth && fontSize < DEFAULT_FONT_SIZE) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        if (
            contentResponsivenessOption === RESIZE_CAROUSEL ||
            contentResponsivenessOption === CROP_CONTENT
        ) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        return fontSize;
    };
    const isCropContentImageOnly =
        contentResponsivenessOption === CROP_CONTENT && text.trim() === "";

    return (
        <div className={classes.statement} id={id} ref={containerRef}>
            {imageUrl && (
                <span className="image-wrapper">
                    <img
                        className={`fade-in-element image-element ${
                            contentResponsivenessOption === CROP_CONTENT ? "crop-image" : ""
                        } ${isCropContentImageOnly ? "cropContentImageOnly" : ""}`}
                        src={imageUrl}
                        ref={imageRef}
                        alt={imageUrl}
                    />
                </span>
            )}
            {text.trim() && (
                <div className="statementTextContent fade-in-element" ref={textContainerRef}>
                    <ClampLines
                        id="statementTextContent"
                        text={text.trim()}
                        lines={linesToShow}
                        ellipsis="..."
                        buttons={false}
                        key={forceUpdateKey}
                    />
                </div>
            )}
        </div>
    );
};

const WrapperWithNavigationButtons = ({
    children,
    classes,
    isPrevBtnActive,
    isNextBtnActive,
    handleNavigationButtonClick,
    isMobileView,
}) => {
    return (
        <div className={classes.navigationButtonsPanel}>
            {isMobileView && (
                <div className="fixedWidth">
                    {isPrevBtnActive && (
                        <FontAwesomeIcon
                            icon={["fas", "arrow-left"]}
                            fixedWidth
                            className={classes.navigationButton}
                            onClick={() => handleNavigationButtonClick("prev")}
                        />
                    )}
                </div>
            )}
            {children}
            {isMobileView && (
                <div className="fixedWidth">
                    {isNextBtnActive && (
                        <FontAwesomeIcon
                            icon={["fas", "arrow-right"]}
                            fixedWidth
                            className={classes.navigationButton}
                            onClick={() => {
                                handleNavigationButtonClick("next");
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export function CarrouselControl(props: ControlProps) {
    const {
        question,
        sequencedVariables,
        validateSignal,
        mentions,
        questionnaire,
        isDesktopPreviewView,
    } = props;
    const questionHasAtleastOneVariable = sequencedVariables && sequencedVariables.length > 0;

    // This condition only occurs if all the statements is eliminated then only question description is displayed
    if (!questionHasAtleastOneVariable) {
        return "";
    }

    const instanceId = `${question.id}_statement`;

    let hideVariableBackArrow = false;
    let canLoopVariable = false;
    let advanceAutomatically = false;
    let choiceListAnswerType = QuestionType[QuestionType.CheckBox];
    let activeStatementMentionsCount = 0;
    const questionName = question.name;
    const dispatch = useDispatch();

    if (hasSetting(question, "HideVariableBackArrow")) {
        hideVariableBackArrow = getSettingBoolean(question, "HideVariableBackArrow");
    }

    if (hasSetting(question, "LoopVariable")) {
        canLoopVariable = getSettingBoolean(question, "LoopVariable");
    }

    if (hasSetting(question, "AutoAdvanceVariable")) {
        advanceAutomatically = getSettingBoolean(question, "AutoAdvanceVariable");
    }

    if (hasSetting(question, "CarrouselChoiceListAnswerType")) {
        choiceListAnswerType = getSettingString(question, "CarrouselChoiceListAnswerType");
    }

    const backButtonAlwaysVisible = canLoopVariable && !hideVariableBackArrow;
    const [language] = useLanguage();
    const theme = useTheme();
    const counter = React.useRef(1);
    const containerRef = React.useRef<HTMLDivElement>();
    const [deviceWidth] = React.useState<number>(window.innerWidth);
    const mobilePreview = useSelector((state: StoreState) => state.mobilePreview);
    const isMobileView = !isDesktopPreviewView && (deviceWidth <= 768 || mobilePreview);
    const justifyContent = isMobileView ? "space-between" : "center";
    const classes = useStyles({ theme, isMobileView, justifyContent });
    const [statementValidation, setStatementValidationCheck] = React.useState({
        action: "NONE",
        direction: "NONE",
    });
    const isPrevBtnActive = useSelector(
        (state: StoreState) => state.interview.carrousel[instanceId]?.isPrevBtnActive || false,
    );
    const isNextBtnActive = useSelector(
        (state: StoreState) => state.interview.carrousel[instanceId]?.isNextBtnActive || false,
    );
    const carrouselActiveVariableIndex = useSelector(
        (state: StoreState) => state.interview.carrousel[instanceId]?.activeVariableIndex || 0,
    );

    let activeVariable = sequencedVariables[carrouselActiveVariableIndex];

    // This condition occurs if due to statement elimination some variable got removed and greater active index is previously stored
    if (!activeVariable) {
        if (questionHasAtleastOneVariable && carrouselActiveVariableIndex !== 0) {
            // dispatch will be triggered with some delay then exception so setting active variable here
            activeVariable = sequencedVariables[0];
            dispatch(
                setCarrouselActiveVariableIndex(
                    instanceId,
                    0,
                    backButtonAlwaysVisible,
                    canLoopVariable || counter.current !== sequencedVariables.length,
                ),
            );
        } else {
            return "";
        }
    }

    const variableHasChoices =
        activeVariable && activeVariable.choiceList && activeVariable.choiceList.length > 0;
    const variableName = activeVariable.name;

    const isRequired = isAnswerRequired(question, questionnaire);
    const defaultMinRequiredAnswerValue = 1;

    const getMinRequiredAnswers = React.useCallback(() => {
        const number = getSettingNumber(question, "MinRequiredAnswers");
        if (number !== undefined && !Number.isNaN(number)) {
            return number === 0 ? defaultMinRequiredAnswerValue : number;
        }
        return null;
    }, [question, defaultMinRequiredAnswerValue]);

    const minRequiredAnswers = getMinRequiredAnswers();

    if (
        mentions &&
        Object.keys(mentions).length > 0 &&
        mentions[variableName] &&
        mentions[variableName].length > 0
    ) {
        activeStatementMentionsCount = mentions[variableName].length;
    }
    const activeStatementHasRequiredMentions =
        activeStatementMentionsCount >= (minRequiredAnswers ?? defaultMinRequiredAnswerValue);

    React.useEffect(() => {
        if (statementValidation.action === "VALIDATEMENTIONS") {
            if (activeStatementHasRequiredMentions) {
                scrollHandler(statementValidation.direction, sequencedVariables);
                setStatementValidationCheck({
                    action: "MENTIONSVALIDATEDSUCCESSFULLY",
                    direction: "NONE",
                });
                dispatch(validationClear(true, false, [questionName]));
            } else {
                setStatementValidationCheck({
                    action: "SHOWMENTIONERROR",
                    direction: "NONE",
                });

                const errorKey =
                    activeStatementMentionsCount === 0 ? "IncompleteAnswer" : "InvalidChoicesMin";

                const minChoices = minRequiredAnswers ?? defaultMinRequiredAnswerValue;

                const questionValidationError = {
                    [questionName]: [
                        { key: errorKey, softPrompt: false, args: { min: minChoices } },
                    ],
                };

                dispatch(
                    validationSetResult({
                        errorsByVariableName: questionValidationError,
                        isValid: false,
                    }),
                );
            }
        }
    }); // Runs every time cause mention might get changed

    React.useEffect(() => {
        const index = counter.current - 1;
        const canLoop = canLoopVariable || counter.current !== sequencedVariables.length;

        dispatch(
            setCarrouselActiveVariableIndex(instanceId, index, backButtonAlwaysVisible, canLoop),
        );
    }, []); // Runs once on component mount

    const scrollHandler = (direction, vars) => {
        const isPrev = direction === "prev";

        // eslint-disable-next-line no-nested-ternary
        counter.current = isPrev
            ? canLoopVariable && counter.current === 1
                ? vars.length
                : counter.current - 1
            : canLoopVariable && counter.current === vars.length
            ? 1
            : counter.current + 1;

        const newPrevBtnActive = isPrev
            ? backButtonAlwaysVisible || counter.current > 1
            : !hideVariableBackArrow;

        const newNextBtnActive = canLoopVariable || counter.current !== sequencedVariables.length;
        dispatch(
            setCarrouselActiveVariableIndex(
                instanceId,
                counter.current - 1,
                newPrevBtnActive,
                newNextBtnActive,
            ),
        );
        dispatch(validationClear(true, false, [questionName]));
    };

    const handleNavigationButtonClick = React.useCallback(
        (direction, isAfterSelectionAction = false) => {
            // If no choices is there then no validation check on arrow click (Can be due to elimination process)
            if (variableHasChoices) {
                const notRequiredButMinChoiceIsDefinedValidation =
                    !isRequired &&
                    minRequiredAnswers &&
                    activeStatementMentionsCount !== 0 &&
                    !activeStatementHasRequiredMentions;

                // For automatically advance(radio only) and prev button click move to next statement
                if (isAfterSelectionAction || direction === "prev") {
                    scrollHandler(direction, sequencedVariables);
                } else if (isRequired || notRequiredButMinChoiceIsDefinedValidation) {
                    setStatementValidationCheck({
                        action: "VALIDATEMENTIONS",
                        direction,
                    });
                } else {
                    scrollHandler(direction, sequencedVariables);
                }
            } else {
                scrollHandler(direction, sequencedVariables);
            }
        },
        [
            isRequired,
            sequencedVariables,
            minRequiredAnswers,
            activeStatementMentionsCount,
            activeStatementHasRequiredMentions,
            variableHasChoices,
        ],
    );

    const onAfterCarouselChoiceSelection = React.useCallback(
        (isAfterSelectionAction) => {
            if (
                advanceAutomatically &&
                choiceListAnswerType === QuestionType[QuestionType.RadioButton]
            ) {
                if (sequencedVariables.length - 1 > carrouselActiveVariableIndex) {
                    handleNavigationButtonClick("next", isAfterSelectionAction);
                }
            }
        },
        [
            sequencedVariables,
            carrouselActiveVariableIndex,
            advanceAutomatically,
            choiceListAnswerType,
            handleNavigationButtonClick,
        ],
    );

    useValidation(validateSignal, useDiscreteVariadicValidator(question, sequencedVariables));

    const commonProps = {
        isNextBtnActive,
        isPrevBtnActive,
        handleNavigationButtonClick,
        classes,
    };

    return (
        <div className={classes.container} ref={containerRef}>
            <WrapperWithNavigationButtons {...commonProps} isMobileView={!isMobileView}>
                <div className={classes.statementsContainer}>
                    <Statement
                        id={activeVariable.elementId}
                        key={activeVariable.name}
                        color={getSettingString(activeVariable, "Color", "#F2F2F2")}
                        text={translate(activeVariable, "text", language)}
                        imageUrl={translate(activeVariable, "imageUrl", language)}
                        isMobileView={isMobileView}
                    />
                </div>
            </WrapperWithNavigationButtons>
            <WrapperWithNavigationButtons {...commonProps} isMobileView={isMobileView}>
                <div className="fixedWidth">{`${carrouselActiveVariableIndex + 1}/${
                    sequencedVariables.length
                }`}</div>
            </WrapperWithNavigationButtons>
            <ControlContainer
                controlType={ChoiceListControl}
                onAfterSelection={() => {
                    onAfterCarouselChoiceSelection(true);
                }}
                isSingleOnPage={props.isSingleOnPage}
                onNavigation={props.onNavigation}
                {...getRendererProps(props)}
            />
        </div>
    );
}
